import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { ApiError, apiBlob, apiFetch } from "@/lib/api/client";
import type { SupportMessage } from "@/types/api";

export type SupportAttachment = NonNullable<
  SupportMessage["attachments"]
>[number];

export const SUPPORT_ATTACHMENT_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const SUPPORT_ATTACHMENT_MAX_BYTES = 8 * 1024 * 1024;
export const SUPPORT_ATTACHMENT_LIMIT = 8;
const STORED_MAX_BYTES = 2 * 1024 * 1024;
const ATTACHMENT_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const supportAttachmentAllowed = (file: File) =>
  SUPPORT_ATTACHMENT_TYPES.includes(file.type) &&
  file.size >= 1 &&
  file.size <= SUPPORT_ATTACHMENT_MAX_BYTES;

export const supportAttachmentsRefused = (error: unknown) =>
  error instanceof ApiError &&
  ((error.status === 404 && error.code === "attachment_not_found") ||
    (error.status === 409 && error.code === "attachment_expired"));

const encodedFilename = (name: string) => {
  try {
    return encodeURIComponent(name);
  } catch {
    return "";
  }
};

export function useUploadSupportAttachment() {
  return useCallback(async (file: File, signal?: AbortSignal): Promise<SupportAttachment> => {
    if (!supportAttachmentAllowed(file)) throw new Error("attachment_refused");
    const filename = encodedFilename(file.name);
    const result = await apiFetch<SupportAttachment>(
      "/me/support/attachments",
      {
        method: "POST",
        signal,
        body: file,
        headers: {
          "Content-Type": file.type,
          ...(filename ? { "X-Attachment-Filename": filename } : {}),
        },
      },
    );
    if (
      !ATTACHMENT_ID.test(String(result?.id)) ||
      result.content_type !== "image/jpeg" ||
      typeof result.filename !== "string" ||
      result.filename.length < 1 ||
      result.filename.length > 255 ||
      !Number.isInteger(result.size_bytes) ||
      result.size_bytes < 1 ||
      result.size_bytes > STORED_MAX_BYTES
    )
      throw new Error("attachment_unconfirmed");
    return {
      id: result.id,
      filename: result.filename,
      content_type: result.content_type,
      size_bytes: result.size_bytes,
    };
  }, []);
}

export function useReadSupportAttachment() {
  return useMutation({
    mutationFn: async (attachmentId: string) => {
      if (!ATTACHMENT_ID.test(attachmentId))
        throw new Error("attachment_unavailable");
      const blob = await apiBlob(`/me/support/attachments/${attachmentId}`);
      if (
        blob.type !== "image/jpeg" ||
        blob.size < 1 ||
        blob.size > STORED_MAX_BYTES
      )
        throw new Error("attachment_unverified");
      return blob;
    },
    retry: false,
    gcTime: 0,
  });
}

export function useReleaseSupportAttachment() {
  return useCallback(async (attachmentId: string, signal: AbortSignal): Promise<void> => {
    if (!ATTACHMENT_ID.test(attachmentId))
      throw new Error("attachment_unavailable");
    await apiFetch<void>(`/me/support/attachments/${attachmentId}`, {
      method: "DELETE",
      signal,
    });
  }, []);
}
